import {
  ActionError,
  AttentionBlock,
  Button,
  ChoiceRow,
  ConfirmDialog,
  Field,
  FieldGrid,
  FormBlock,
  Input,
  FormFooter,
  PageHeader,
  PageTitle,
  Radio,
  RadioGroup,
  ReadOnlyField,
  ReadingColumn,
  StatusTag,
  Textarea,
  formatDate,
  formatRelative,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { UserSessionsBlock } from '@/features/account/user-sessions-block'
import { FormSkeleton } from '@/features/clinic-profile/blocks'
import { personNameError } from '@/features/clinic-profile/form'
import { focusFirstInvalid } from '@/features/clinic-profile/use-form-state'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  useChangeRole,
  useRemoveUser,
  useResendInvite,
  useResetPassword,
  useSetUserAccess,
  useUpdateUser,
  useUser,
  type ClinicUser,
  type MemberRole,
  type PasswordReset,
} from './api'
import {
  MSG_JUSTIFICATION,
  REMOVE_ANCHOR,
  ROLES,
  ROLE_LABELS,
  canSendInvite,
  hasPendingInvite,
  isJustification,
  userStatus,
} from './labels'
import { ActionCard } from './action-card'
import { RoleFields } from './role-fields'
import { TemporaryPassword } from './temporary-password'

/**
 * Editar usuário (docs/areas/administracao.md, "Editar usuário"; RN-ADM-11 a 15). Arquétipo:
 * cadastro. Salvar grava o nome e o acesso (bloquear ou liberar); o que é arriscado fica em cartões
 * à parte: resetar senha ou reenviar convite, trocar papel e remover. A própria conta e o vínculo
 * já removido não têm essas ações.
 */

type Access = 'active' | 'blocked'
type Panel = 'role' | 'remove' | null

/** Um aviso por vez: o que falhou, perto de onde a pessoa agiu. */
interface Failure {
  where: 'form' | 'cards'
  title: string
  description: ReactNode
}

const hintClass =
  'font-body text-eyebrow text-label mt-[6px] leading-[1.5] font-light tracking-normal'

export function UserPage() {
  const { id } = useParams()
  const query = useUser(id)
  // O formulário nasce do vínculo carregado; outro id, outro formulário.
  return <UserForm key={query.data?.id ?? 'loading'} id={id ?? ''} query={query} />
}

function UserForm({ id, query }: { id: string; query: ReturnType<typeof useUser> }) {
  const navigate = useNavigate()
  const location = useLocation()
  const session = useSession()
  const user = query.data
  const update = useUpdateUser(id)
  const setAccess = useSetUserAccess(id)
  const reset = useResetPassword(id)
  const resend = useResendInvite(id)
  const changeRole = useChangeRole(id)
  const remove = useRemoveUser(id)
  const formRef = useRef<HTMLFormElement>(null)

  const currentAccess: Access = user?.status === 'blocked' ? 'blocked' : 'active'
  const [name, setName] = useState(user?.name ?? '')
  const [nameTouched, setNameTouched] = useState(false)
  const [nameRefused, setNameRefused] = useState<string | undefined>()
  const [access, setAccessChoice] = useState<Access>(currentAccess)
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)

  // "remover" na lista chega aqui com a âncora: o cartão já abre com a justificativa em foco.
  const [panel, setPanel] = useState<Panel>(location.hash === `#${REMOVE_ANCHOR}` ? 'remove' : null)
  const [confirmingReset, setConfirmingReset] = useState(false)
  const [passwordReset, setPasswordReset] = useState<PasswordReset | null>(null)
  const [newRole, setNewRole] = useState<MemberRole | null>(null)
  const [newPractitionerId, setNewPractitionerId] = useState('')
  const [roleJustification, setRoleJustification] = useState('')
  const [roleAttempted, setRoleAttempted] = useState(false)
  const [roleRefused, setRoleRefused] = useState<Record<string, string>>({})
  const [removeJustification, setRemoveJustification] = useState('')
  const [removeAttempted, setRemoveAttempted] = useState(false)
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  const displayName = user ? titleCase(user.name) : 'Usuário'
  const isArchived = Boolean(user && (user.archivedAt !== null || user.status === 'removed'))
  const isBlocked = user?.status === 'blocked'
  // Ninguém bloqueia, remove, troca o papel nem reseta a senha da própria conta (RN-ADM-13 e 14).
  const isSelf = Boolean(user && user.userId === session.profile?.userId)
  const canEdit = Boolean(user) && !isArchived

  const nameError = nameRefused ?? (nameTouched ? personNameError(name) : undefined)

  async function save(event: FormEvent) {
    event.preventDefault()
    if (!user || !canEdit) return
    setNameTouched(true)
    if (personNameError(name)) {
      focusFirstInvalid(formRef.current)
      return
    }
    const nextName = name.trim()
    const nameChanged = nextName !== user.name
    const accessChanged = !isSelf && access !== currentAccess
    if (!nameChanged && !accessChanged) {
      toast('Nenhuma alteração para salvar')
      return
    }

    setFailure(null)
    setSaving(true)
    let nameSaved = false
    try {
      if (nameChanged) {
        await update.mutateAsync(nextName)
        nameSaved = true
      }
      if (accessChanged) await setAccess.mutateAsync(access === 'blocked' ? 'block' : 'unblock')
      const savedName = titleCase(nextName)
      if (!accessChanged) toast(`Nome de ${savedName} salvo`)
      else if (access === 'blocked') {
        toast(`Acesso bloqueado. ${savedName} não entra mais nesta clínica.`)
      } else toast(`Acesso liberado. ${savedName} já pode entrar.`)
    } catch (error) {
      if (nameChanged && !nameSaved) {
        const refused = fieldErrors(error).name
        if (refused) {
          setNameRefused(refused)
          focusFirstInvalid(formRef.current)
        } else {
          setFailure({
            where: 'form',
            title: 'Não foi possível salvar o usuário',
            description: `${errorMessage(error)} O que você digitou continua na tela.`,
          })
        }
      } else {
        // O acesso volta a mostrar o que vale de verdade; o motivo fica escrito.
        setAccessChoice(currentAccess)
        setFailure({
          where: 'form',
          title:
            access === 'blocked'
              ? 'Não foi possível bloquear o acesso'
              : 'Não foi possível liberar o acesso',
          description: `${errorMessage(error)}${nameSaved ? ' O nome foi salvo.' : ''}`,
        })
      }
    } finally {
      setSaving(false)
    }
  }

  function confirmReset() {
    reset.mutate(undefined, {
      onSuccess: (result) => {
        setConfirmingReset(false)
        setFailure(null)
        setPasswordReset(result)
      },
      onError: (error) => {
        setConfirmingReset(false)
        setFailure({
          where: 'cards',
          title: 'Não foi possível resetar a senha',
          description: errorMessage(error),
        })
      },
    })
  }

  function resendInvite() {
    if (!user) return
    resend.mutate(undefined, {
      onSuccess: (result) => {
        if (result.emailSent) {
          setFailure(null)
          toast(`Convite reenviado a ${user.email} — vale 7 dias`)
          return
        }
        setFailure({
          where: 'cards',
          title: 'O convite foi criado, mas o e-mail não saiu',
          description: `Reenvie o convite em instantes. Se continuar falhando, confira o endereço ${user.email}: o e-mail não muda depois de criado, só removendo o usuário e criando outro.`,
        })
      },
      onError: (error) =>
        setFailure({
          where: 'cards',
          title: 'Não foi possível reenviar o convite',
          description: errorMessage(error),
        }),
    })
  }

  function openPanel(next: Panel) {
    setFailure(null)
    setPanel(next)
  }

  const roleErrors = {
    role: roleRefused.role ?? (roleAttempted && !newRole ? 'Escolha o perfil novo.' : undefined),
    practitionerId:
      roleRefused.practitionerId ??
      (roleAttempted && newRole === 'practitioner' && newPractitionerId === ''
        ? 'Escolha qual prestadora esta pessoa é.'
        : undefined),
    justification:
      roleRefused.justification ??
      (roleAttempted && !isJustification(roleJustification) ? MSG_JUSTIFICATION : undefined),
  }

  function submitRole() {
    setRoleAttempted(true)
    if (!newRole || !isJustification(roleJustification)) return
    if (newRole === 'practitioner' && newPractitionerId === '') return
    setFailure(null)
    changeRole.mutate(
      {
        role: newRole,
        ...(newRole === 'practitioner' ? { practitionerId: newPractitionerId } : {}),
        justification: roleJustification.trim(),
      },
      {
        onSuccess: (replacement) => {
          toast(`${displayName} agora entra como ${ROLE_LABELS[replacement.role].toLowerCase()}`)
          // O vínculo antigo foi arquivado: a tela segue para o novo.
          navigate(routes.admin.user(replacement.id), { replace: true })
        },
        onError: (error) => {
          const { role, practitionerId, justification } = fieldErrors(error)
          const refused = {
            ...(role ? { role } : {}),
            ...(practitionerId ? { practitionerId } : {}),
            ...(justification ? { justification } : {}),
          }
          setRoleRefused(refused)
          if (Object.keys(refused).length === 0) {
            setFailure({
              where: 'cards',
              title: 'Não foi possível trocar o papel',
              description: errorMessage(error),
            })
          }
        },
      },
    )
  }

  function askRemove() {
    setRemoveAttempted(true)
    if (isJustification(removeJustification)) setConfirmingRemove(true)
  }

  function confirmRemove() {
    remove.mutate(removeJustification.trim(), {
      onSuccess: () => {
        toast(`${displayName} não entra mais nesta clínica — o histórico continua registrado`)
        navigate(routes.admin.users)
      },
      onError: (error) => {
        setConfirmingRemove(false)
        setFailure({
          where: 'cards',
          title: 'Não foi possível remover o usuário',
          description: errorMessage(error),
        })
      },
    })
  }

  /** A alternativa do modal de remover: bloquear mantém o vínculo e pode ser revertido. */
  function blockInstead() {
    setConfirmingRemove(false)
    setAccess.mutate('block', {
      onSuccess: () => {
        setFailure(null)
        setPanel(null)
        setAccessChoice('blocked')
        toast(`Acesso bloqueado. ${displayName} não entra mais nesta clínica.`)
      },
      onError: (error) =>
        setFailure({
          where: 'cards',
          title: 'Não foi possível bloquear o acesso',
          description: errorMessage(error),
        }),
    })
  }

  const practitionerName = user?.practitioner?.name
  const failureBlock = (where: Failure['where']) =>
    failure?.where === where ? (
      <ActionError
        title={failure.title}
        description={failure.description}
        onDismiss={() => setFailure(null)}
      />
    ) : null

  return (
    <form ref={formRef} onSubmit={(event) => void save(event)} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Usuários', href: routes.admin.users },
          { label: user ? displayName : '…' },
        ]}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to={routes.admin.users}>Voltar</Link>
          </Button>
        }
      />
      <Page>
        <ReadingColumn wide>
          {query.isError ? (
            <>
              <PageTitle title="Editar usuário" />
              <LoadError
                className="mt-6"
                title="Não conseguimos abrir este usuário"
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            </>
          ) : !user ? (
            <>
              <PageTitle title="Editar usuário" />
              <FormSkeleton blocks={2} />
            </>
          ) : (
            <>
              <PageTitle
                title={isArchived ? 'Usuário removido' : 'Editar usuário'}
                meta={identificationLine(user)}
                trailing={
                  <StatusTag level={userStatus(user).level}>{userStatus(user).label}</StatusTag>
                }
              />

              <div className="mt-6 flex flex-col gap-4">
                {isArchived ? (
                  <AttentionBlock
                    variant="lilac"
                    title={
                      user.archivedAt
                        ? `Removido em ${formatDate(new Date(user.archivedAt))}`
                        : 'Removido desta clínica'
                    }
                  >
                    <p>
                      Este vínculo foi removido ou substituído por uma troca de papel. Tudo o que a
                      pessoa fez continua registrado. Para ela voltar a entrar, crie o usuário de
                      novo com o mesmo e-mail.
                    </p>
                    <Button className="mt-3" variant="outline" asChild>
                      <Link to={routes.admin.newUser}>Criar usuário</Link>
                    </Button>
                  </AttentionBlock>
                ) : null}

                {failureBlock('form')}

                <FormBlock
                  title="Dados do usuário"
                  note="O nome aparece na trilha de ações e nos documentos que a pessoa assina pela clínica."
                >
                  <div className="flex flex-col gap-4">
                    <FieldGrid>
                      {canEdit ? (
                        <Field label="Nome" error={nameError}>
                          <Input
                            autoComplete="off"
                            maxLength={120}
                            value={name}
                            onChange={(event) => {
                              setName(event.target.value)
                              setNameRefused(undefined)
                            }}
                            onBlur={() => setNameTouched(true)}
                          />
                        </Field>
                      ) : (
                        <ReadOnlyField label="Nome" value={displayName} />
                      )}
                      <ReadOnlyField
                        label="E-mail"
                        value={<span className="break-all">{user.email}</span>}
                      />
                    </FieldGrid>

                    {isArchived ? null : isSelf ? (
                      <ReadOnlyField label="Acesso" value="Ativo" />
                    ) : (
                      <div>
                        <div className="eyebrow" id="user-access-label">
                          Acesso
                        </div>
                        <RadioGroup
                          aria-labelledby="user-access-label"
                          className="tablet:grid-cols-2 mt-[6px]"
                          value={access}
                          onValueChange={(value) => setAccessChoice(value as Access)}
                        >
                          <ChoiceRow
                            control={<Radio value="active" />}
                            description="Entra nesta clínica com a permissão abaixo."
                          >
                            Ativo
                          </ChoiceRow>
                          <ChoiceRow
                            control={<Radio value="blocked" />}
                            description="Não entra nesta clínica. Vale ao salvar; o histórico fica, e dá para liberar depois."
                          >
                            Bloqueado
                          </ChoiceRow>
                        </RadioGroup>
                        {isBlocked ? <p className={hintClass}>{blockedLine(user)}</p> : null}
                      </div>
                    )}

                    <div>
                      <ReadOnlyField
                        label="Permissão"
                        note="· definida na criação"
                        value={
                          practitionerName
                            ? `${ROLE_LABELS[user.role]} · ${titleCase(practitionerName)}`
                            : ROLE_LABELS[user.role]
                        }
                      />
                      {isArchived ? null : (
                        <p className={hintClass}>
                          Para mudar de papel, remova e crie novamente — o histórico de ações fica
                          preservado.
                        </p>
                      )}
                    </div>
                  </div>
                </FormBlock>

                {canEdit ? (
                  <FormFooter
                    secondary={
                      <Button variant="outline" asChild>
                        <Link to={routes.admin.users}>Cancelar</Link>
                      </Button>
                    }
                    primary={
                      <Button type="submit" loading={saving} loadingLabel="Salvando…">
                        Salvar
                      </Button>
                    }
                  />
                ) : null}

                {isArchived ? null : isSelf ? (
                  <AttentionBlock variant="lilac" title="Esta é a sua conta">
                    <p>
                      Você não pode bloquear, remover ou mudar o papel da própria conta: peça a
                      outra gestora. Para trocar a senha, abra{' '}
                      <Link
                        to={routes.accountTab('password')}
                        className="text-ink border-hairline-heavy hover:border-ink border-b"
                      >
                        Sua conta
                      </Link>
                      .
                    </p>
                  </AttentionBlock>
                ) : (
                  <>
                    {failureBlock('cards')}

                    {passwordReset ? (
                      <TemporaryPassword
                        name={user.name}
                        password={passwordReset.temporaryPassword}
                      >
                        Esta senha não aparece de novo. Copie e entregue a {user.email}; a troca é
                        obrigatória no próximo acesso. As sessões abertas foram encerradas.
                      </TemporaryPassword>
                    ) : hasPendingInvite(user) ? (
                      <ActionCard
                        title="Reenviar convite"
                        description={
                          isBlocked
                            ? `${displayName} está com o acesso bloqueado. Libere o acesso antes de reenviar o convite.`
                            : inviteLine(user)
                        }
                        action={
                          <Button
                            variant="outline"
                            disabled={isBlocked}
                            loading={resend.isPending}
                            loadingLabel="Reenviando…"
                            onClick={resendInvite}
                          >
                            Reenviar convite
                          </Button>
                        }
                      />
                    ) : (
                      <ActionCard
                        title="Resetar senha"
                        description={
                          isBlocked
                            ? `${displayName} está com o acesso bloqueado. Libere o acesso antes de resetar a senha.`
                            : 'Gera uma senha temporária; a troca é obrigatória no próximo acesso.'
                        }
                        action={
                          <Button
                            variant="outline"
                            disabled={isBlocked}
                            onClick={() => setConfirmingReset(true)}
                          >
                            Resetar
                          </Button>
                        }
                      />
                    )}

                    {!passwordReset && canSendInvite(user) && !isBlocked ? (
                      <ActionCard
                        title="Enviar convite por e-mail"
                        description={`${displayName} ainda não definiu a senha. O convite leva à criação da senha pelo e-mail e vale 7 dias; quem não recebeu senha temporária só entra por ele.`}
                        action={
                          <Button
                            variant="outline"
                            loading={resend.isPending}
                            loadingLabel="Enviando…"
                            onClick={resendInvite}
                          >
                            Enviar convite
                          </Button>
                        }
                      />
                    ) : null}

                    <ActionCard
                      title="Trocar papel"
                      description={
                        isBlocked
                          ? `${displayName} está com o acesso bloqueado. Libere o acesso antes de trocar o papel.`
                          : 'Arquiva o vínculo atual e cria outro com o papel novo, numa operação só. As sessões abertas da pessoa são encerradas; o histórico de ações fica preservado.'
                      }
                      action={
                        panel === 'role' ? null : (
                          <Button
                            variant="outline"
                            disabled={isBlocked}
                            onClick={() => openPanel('role')}
                          >
                            Trocar papel
                          </Button>
                        )
                      }
                    >
                      {panel === 'role' && !isBlocked ? (
                        <>
                          <RoleFields
                            label="Permissão nova"
                            roles={ROLES.filter((role) => role !== user.role)}
                            role={newRole}
                            practitionerId={newPractitionerId}
                            onRole={(role) => {
                              setNewRole(role)
                              setNewPractitionerId('')
                              setRoleRefused({})
                            }}
                            onPractitioner={(practitionerId) => {
                              setNewPractitionerId(practitionerId)
                              setRoleRefused(({ practitionerId: _refused, ...rest }) => rest)
                            }}
                            errors={roleErrors}
                          />
                          <Field
                            label="Justificativa"
                            meta={`${roleJustification.trim().length}/500`}
                            error={roleErrors.justification}
                            hint="Fica na trilha da clínica, com o seu nome e a data."
                          >
                            <Textarea
                              value={roleJustification}
                              maxLength={500}
                              onChange={(event) => {
                                setRoleJustification(event.target.value)
                                setRoleRefused(({ justification: _refused, ...rest }) => rest)
                              }}
                            />
                          </Field>
                          <div className="flex flex-wrap items-center gap-4">
                            <Button
                              variant="outline"
                              loading={changeRole.isPending}
                              loadingLabel="Trocando o papel…"
                              onClick={submitRole}
                            >
                              Confirmar a troca de papel
                            </Button>
                            <Button variant="text" onClick={() => openPanel(null)}>
                              Desistir da troca
                            </Button>
                          </div>
                        </>
                      ) : null}
                    </ActionCard>

                    <ActionCard
                      id={REMOVE_ANCHOR}
                      title="Remover da clínica"
                      description="A pessoa perde o acesso a esta clínica na hora e mantém o acesso às outras. Nada do que ela fez é apagado. O vínculo removido não volta: para a pessoa retornar, cria-se o usuário de novo."
                      action={
                        panel === 'remove' ? null : (
                          <Button variant="outline" onClick={() => openPanel('remove')}>
                            Remover
                          </Button>
                        )
                      }
                    >
                      {panel === 'remove' ? (
                        <>
                          <Field
                            label="Justificativa"
                            meta={`${removeJustification.trim().length}/500`}
                            error={
                              removeAttempted && !isJustification(removeJustification)
                                ? MSG_JUSTIFICATION
                                : undefined
                            }
                            hint="Fica na trilha da clínica, com o seu nome e a data."
                          >
                            <Textarea
                              autoFocus
                              value={removeJustification}
                              maxLength={500}
                              onChange={(event) => setRemoveJustification(event.target.value)}
                            />
                          </Field>
                          <div className="flex flex-wrap items-center gap-4">
                            <Button variant="outline" onClick={askRemove}>
                              Remover da clínica
                            </Button>
                            <Button variant="text" onClick={() => openPanel(null)}>
                              Desistir de remover
                            </Button>
                          </div>
                        </>
                      ) : null}
                    </ActionCard>
                  </>
                )}

                {/* Os últimos acessos da conta, sem IP (RN-AUT-25, P-AUT-28). */}
                <UserSessionsBlock userId={user.userId} name={displayName} />
              </div>
            </>
          )}
        </ReadingColumn>
      </Page>

      <ConfirmDialog
        open={confirmingReset}
        onOpenChange={setConfirmingReset}
        title={`Resetar a senha de ${displayName}?`}
        effect="As sessões abertas desta pessoa são encerradas agora, e a senha atual deixa de valer. Você recebe uma senha temporária para entregar; a troca é obrigatória no próximo acesso."
        confirmLabel="Resetar"
        loading={reset.isPending}
        loadingLabel="Resetando…"
        onConfirm={confirmReset}
      />

      <ConfirmDialog
        open={confirmingRemove}
        onOpenChange={setConfirmingRemove}
        title={`Remover ${displayName} desta clínica?`}
        effect="A pessoa perde o acesso agora. Tudo o que ela fez (documentos, planos, fotos) continua registrado em nome dela."
        alternative={
          isBlocked
            ? undefined
            : {
                description: 'Alternativa: bloquear, que mantém o vínculo e pode ser revertido.',
                label: 'Bloquear',
                onSelect: blockInstead,
              }
        }
        confirmLabel="Remover"
        loading={remove.isPending}
        loadingLabel="Removendo…"
        onConfirm={confirmRemove}
      />
    </form>
  )
}

/** "Recepção · criado em 21/03/2026 por Gabriel Ribeiro Marques · último acesso há 2 dias". */
function identificationLine(user: ClinicUser) {
  return [
    ROLE_LABELS[user.role],
    `criado em ${formatDate(new Date(user.createdAt))} por ${titleCase(user.createdBy.name)}`,
    lastSignInLine(user.lastSignInAt),
  ].join(' · ')
}

/** Tempo relativo até 7 dias, data depois disso (docs/styleguide/04-escrita.md). */
function lastSignInLine(lastSignInAt: string | null) {
  if (!lastSignInAt) return 'ainda não entrou'
  const relative = formatRelative(new Date(lastSignInAt))
  return /^\d/.test(relative) ? `último acesso em ${relative}` : `último acesso ${relative}`
}

/** "Bloqueado em 01/09/2026 por Gabriel Ribeiro Marques." */
function blockedLine(user: ClinicUser) {
  const when = user.blockedAt ? ` em ${formatDate(new Date(user.blockedAt))}` : ''
  const who = user.blockedBy ? ` por ${titleCase(user.blockedBy.name)}` : ''
  return `Bloqueado${when}${who}.`
}

/** Até quando o convite vale, ou desde quando venceu, e o que reenviar faz. */
function inviteLine(user: ClinicUser, now = new Date()) {
  const expiresAt = new Date(user.inviteExpiresAt ?? now)
  const validity =
    expiresAt.getTime() < now.getTime()
      ? `O convite enviado a ${user.email} venceu em ${formatDate(expiresAt)}.`
      : `O convite enviado a ${user.email} vale até ${formatDate(expiresAt)}.`
  return `${validity} Reenviar gera um link novo, válido por 7 dias; o anterior deixa de valer.`
}
